import React, { lazy, Suspense, useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType, Navigate, Outlet, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
// --- Eagerly loaded shell (needed on first paint / on every route) ---
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Navbar from "@/components/Navbar";
import AuthCallback from "@/components/AuthCallback";
import { InstallPrompt } from "@/components/InstallPrompt";
import CookieConsent from "@/components/CookieConsent";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";
import { FirstApplicationPromptHost } from "@/components/BringAFriendDialog";
import PageviewBeacon from "@/components/PageviewBeacon";
import PwaInstallPing from "@/components/PwaInstallPing";
import { CampaignBanner } from "@/components/CampaignBanner";
import Footer from "@/components/Footer";
import { BackToTop } from "@/components/BackToTop";
import { captureFirstTouch } from "@/lib/attribution";
import { ProductModeContext, envProductMode } from "@/lib/productMode";
// Special-path pages rendered OUTSIDE the <Routes> Suspense boundary — eager.
import ComingSoon from "@/pages/ComingSoon";
import Suspended from "@/pages/Suspended";

// --- Route-level code splitting (perf) ---
// Each page ships as its own chunk, fetched on navigation, so the initial load
// no longer downloads all ~50 pages in one bundle. Rendered under <Suspense>.
const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Opportunities = lazy(() => import("@/pages/Opportunities"));
const OpportunityDetail = lazy(() => import("@/pages/OpportunityDetail"));
const PostOpportunity = lazy(() => import("@/pages/PostOpportunity"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const LogHours = lazy(() => import("@/pages/LogHours"));
const Messages = lazy(() => import("@/pages/Messages"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const Certificate = lazy(() => import("@/pages/Certificate"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const OrgImpact = lazy(() => import("@/pages/OrgImpact"));
const PublicOrg = lazy(() => import("@/pages/PublicOrg"));
const Teams = lazy(() => import("@/pages/Teams"));
const Admin = lazy(() => import("@/pages/Admin"));
const Discover = lazy(() => import("@/pages/Discover"));
const Stories = lazy(() => import("@/pages/Stories"));
const Partners = lazy(() => import("@/pages/Partners"));
const ForProfessionals = lazy(() => import("@/pages/ForProfessionals"));
const CampaignLanding = lazy(() => import("@/pages/CampaignLanding"));
const ChallengePage = lazy(() => import("@/pages/ChallengePage"));
const Explore = lazy(() => import("@/pages/Explore"));
const Recognition = lazy(() => import("@/pages/Recognition"));
const Community = lazy(() => import("@/pages/Community"));
const CommunityPost = lazy(() => import("@/pages/CommunityPost"));
const Pathways = lazy(() => import("@/pages/Pathways"));
const Collectives = lazy(() => import("@/pages/Collectives"));
const CollectiveDetail = lazy(() => import("@/pages/CollectiveDetail"));
const PostHelpRequest = lazy(() => import("@/pages/PostHelpRequest"));
const Donate = lazy(() => import("@/pages/Donate"));
const About = lazy(() => import("@/pages/About"));
const Safety = lazy(() => import("@/pages/Safety"));
const Guide = lazy(() => import("@/pages/Guide"));
const Wrapped = lazy(() => import("@/pages/Wrapped"));
const VolunteeringIn = lazy(() => import("@/pages/VolunteeringIn"));
const CollectivePoster = lazy(() => import("@/pages/CollectivePoster"));
const Press = lazy(() => import("@/pages/Press"));
const ProgrammeGuide = lazy(() => import("@/pages/ProgrammeGuide"));
const TeamQuarterly = lazy(() => import("@/pages/TeamQuarterly"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Safeguarding = lazy(() => import("@/pages/Safeguarding"));
const Street = lazy(() => import("@/pages/Street"));
const ImpactArea = lazy(() => import("@/pages/ImpactArea"));
const ImpactPack = lazy(() => import("@/pages/ImpactPack"));
const VerifyEvidence = lazy(() => import("@/pages/VerifyEvidence"));
const EvidencePackPage = lazy(() => import("@/pages/EvidencePackPage"));

// Fallback shown while a lazily-loaded route chunk is fetched. Matches the
// existing ProtectedRoute loading style so the swap is visually consistent.
function PageLoader() {
  return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">Loading…</div>;
}

function ProtectedRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

// Route changes always start at the top — but in-page anchors (#apply) keep
// working and back/forward (POP) lets the browser restore its own position.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (hash || navType === "POP") return;
    window.scrollTo(0, 0);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps -- scroll reset keys on route change only
  return null;
}

function ShellRoutes() {
  const location = useLocation();
  const { user } = useAuth();
  // Detect OAuth callback synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }  // Phase 8: suspended accounts get a single full-screen view — no navbar,
  // no routes. Backend already 403s everything except /auth/me + appeals.
  if (user?.banned) {
    return <Suspended />;
  }
  // /street is the poster/QR landing page (collectives brief §1.1): one
  // screen, one question. Site chrome would be exactly the "nav clutter"
  // the brief bans above the fold, so the shell steps back on that path —
  // the page carries its own wordmark home link.
  const isStreetDoor = location.pathname === "/street";
  // Inside a collective the street's own shell (header + nav + bottom bar in
  // CollectiveDetail) replaces the global chrome. /collectives/:id/poster keeps it.
  const inCollectiveSite = /^\/collectives\/[^/]+(\/(?!poster$)[^/]+)?\/?$/.test(location.pathname);
  return (
    <>
      <ScrollToTop />
      {!isStreetDoor && !inCollectiveSite && <Navbar />}
      {!isStreetDoor && <CampaignBanner />}
      {!isStreetDoor && <VerifyEmailBanner />}
      <BackToTop />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/opportunities" element={<Opportunities />} />
        <Route path="/opportunities/:id" element={<OpportunityDetail />} />
        <Route path="/stories" element={<Stories />} />
        {/* Phase 17-B item 2: legacy recognition routes redirect to the
            consolidated /recognition tabs (relabel, don't rename URLs). */}
        <Route path="/leaderboard" element={<Navigate to="/recognition?tab=leaderboard" replace />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/for-professionals" element={<ForProfessionals />} />
        <Route path="/campaigns/:slug" element={<CampaignLanding />} />
        <Route path="/challenge/:slug" element={<ChallengePage />} />
        {/* public: safeguarding policy (Tier 1 content, 2026-07) */}
        <Route path="/safeguarding" element={<Safeguarding />} />
        {/* public: printable A5 programme-kit guides (18-B1 item 8) */}
        <Route path="/programme-guides/:slug" element={<ProgrammeGuide />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/awards" element={<Navigate to="/recognition?tab=awards" replace />} />
        <Route path="/badges" element={<Navigate to="/recognition?tab=badges" replace />} />
        <Route path="/recognition" element={<Recognition />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/community" element={<Community />} />
        <Route path="/community/:id" element={<CommunityPost />} />
        <Route path="/pathways" element={<Pathways />} />
        <Route path="/collectives" element={<Collectives />} />
        <Route path="/collectives/:id" element={<CollectiveDetail />} />
        <Route path="/collectives/:id/:section" element={<CollectiveDetail />} />
        {/* Public front door — civichearth.com/street (brief Phase 1). */}
        <Route path="/street" element={<Street />} />
        <Route path="/volunteering-in/:slug" element={<VolunteeringIn />} />
        <Route path="/impact/:slug" element={<ImpactArea />} />
        <Route path="/impact/:slug/pack" element={<ImpactPack />} />
        <Route path="/verify/:code" element={<VerifyEvidence />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/about" element={<About />} />
        <Route path="/safety" element={<Safety />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/u/:userId" element={<PublicProfile />} />
        <Route path="/o/:orgId/impact" element={<OrgImpact />} />
        <Route path="/o/:orgId" element={<PublicOrg />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/wrapped" element={<Wrapped />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/post-opportunity" element={<PostOpportunity />} />
          <Route path="/help-request/new" element={<PostHelpRequest />} />
          <Route path="/opportunities/:editId/edit" element={<PostOpportunity />} />
          <Route path="/log-hours" element={<LogHours />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/certificate/:tierId" element={<Certificate />} />
          <Route path="/collectives/:id/poster" element={<CollectivePoster />} />
          <Route path="/press" element={<Press />} />
          <Route path="/evidence/:id" element={<EvidencePackPage />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/teams" element={<Teams />} />
          <Route path="/teams/:id/quarterly" element={<TeamQuarterly />} />
          <Route path="/admin" element={<Admin />} />
        </Route>
        {/* Catch-all 404 with "did you mean" for common wrong route shapes
            (18-A1 follow-up, upgraded in the 18-B2 polish batch). */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      {/* /discover is a viewport-fit swipe surface on mobile — the footer
          below it would make the page scrollable and fight the gesture.
          /street hides it entirely: a poster landing does one thing. */}
      <div className={(isStreetDoor || inCollectiveSite) ? "hidden" : location.pathname === "/discover" ? "hidden lg:block" : ""}>
        <Footer />
      </div>
    </>
  );
}

function App() {
  // Coming-soon holding mode is backend-driven (COMING_SOON env) so flipping
  // it is a single env change — no frontend rebuild. While the flag loads we
  // paint a neutral blank so the real app never flashes on a gated domain.
  const [comingSoon, setComingSoon] = useState(null);
  const [productMode, setProductMode] = useState(envProductMode());
  useEffect(() => {
    captureFirstTouch(); // 17-C item 9: first-touch attribution (no-op after first visit)
    // Boot-window resilience: retry briefly before failing open, so a gated
    // prod domain doesn't flash the normal app (and preview doesn't need a
    // manual reload) when the first fetch lands during a backend restart.
    let cancelled = false;
    const attempt = (n) => {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/config/public`)
        .then((r) => {
          if (cancelled) return;
          setComingSoon(!!r.data?.coming_soon);
          if (r.data?.product_mode) setProductMode(r.data.product_mode === "collectives" ? "collectives" : "full");
        })
        .catch(() => {
          if (cancelled) return;
          if (n < 2) setTimeout(() => attempt(n + 1), 2500 * (n + 1));
          else setComingSoon(false);
        });
    };
    attempt(0);
    return () => { cancelled = true; };
  }, []);
  if (comingSoon === null) return <div className="min-h-screen bg-cream" />;
  if (comingSoon) {
    return (
      <HelmetProvider>
        <BrowserRouter>
          <ComingSoon />
        </BrowserRouter>
      </HelmetProvider>
    );
  }

  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <ProductModeContext.Provider value={productMode}>
            <ErrorBoundary>
              <ShellRoutes />
            </ErrorBoundary>
            <Toaster richColors position="top-right" />
            <InstallPrompt />
            <CookieConsent />
            <FeedbackWidget />
            <FirstApplicationPromptHost />
            <PageviewBeacon />
            </ProductModeContext.Provider>
            <PwaInstallPing />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
